import { Edit, Facebook, Instagram, Loader2, Twitter } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import useStoreConfig from "../hooks/useStoreConfig";
import { storeService } from "../services/storeService";

interface StoreData {
  brandName: string;
  tagLine: string;
  subTagLine: string;
  logoUrl: string | null;
  bannerUrl: string | null;
  isBannerActive: boolean;
  isStoreActive: boolean;
  socialLinks: {
    facebook: string;
    instagram: string;
    twitter: string;
  };
}

const StoreConfig = () => {
  const navigate = useNavigate();
  const [showBannerStatusDialog, setShowBannerStatusDialog] = useState(false);
  const { data, isLoading, error, refetch } = useStoreConfig();
  const storeData = data as StoreData;
  
  const hasLogo = storeData?.logoUrl && storeData.logoUrl !== "";
  const socialLinks = storeData?.socialLinks ?? {
    facebook: "",
    instagram: "",
    twitter: "",
  };

  const handleToggleBanner = async () => {
    try {
      await storeService.toggleBanner();
      await refetch();
      setShowBannerStatusDialog(false);
    } catch (error) {
      console.error("Error toggling banner:", error);
    }
  };

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="flex items-center gap-3 bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100 w-full max-w-sm">
        <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 animate-spin" />
        <p className="text-sm sm:text-base text-gray-700 font-medium">Loading your store information...</p>
      </div>
    </div>
  );
  
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-red-600 flex items-center gap-3 bg-red-50 px-4 sm:px-8 py-4 sm:py-6 rounded-xl shadow-sm border border-red-100 w-full max-w-md">
        <span className="min-w-2 min-h-2 sm:min-w-3 sm:min-h-3 bg-red-500 rounded-full"></span>
        <span className="text-sm sm:text-base font-medium">Error loading store data. Please try again later.</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-16 sm:pb-12">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0">
            <div className="flex items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Store Configuration</h1>
            </div>
            <button
              onClick={() => navigate("/admin/store/edit")}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm text-sm sm:text-base"
            >
              <Edit className="w-4 h-4" />
              <span className="font-medium">Edit Store Configuration</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 sm:mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Left Column - Store Details */}
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            {/* Store Information Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Store Information</h2>
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <label className="text-sm font-medium text-gray-500 block mb-1.5 sm:mb-2">Brand Name</label>
                    <div className="text-gray-900 font-semibold text-lg sm:text-xl">{storeData.brandName}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 block mb-1.5 sm:mb-2">TagLine</label>
                    <div className="text-gray-700 text-base sm:text-lg">{storeData.tagLine || "No tagLine set"}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 block mb-1.5 sm:mb-2">SubTagLine</label>
                    <div className="text-gray-600 text-sm sm:text-base">{storeData.subTagLine || "No subTagLine set"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Store Assets Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Store Assets</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                  {/* Logo Section */}
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-2 sm:mb-3">Store Logo</label>
                    <div className="w-32 h-32 sm:w-40 sm:h-40 mx-auto bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-center overflow-hidden">
                      {hasLogo ? (
                        <img src={storeData.logoUrl!} alt="Store Logo" className="w-full h-full object-contain p-2 sm:p-3" />
                      ) : (
                        <div className="text-center bg-blue-50 w-full h-full flex items-center justify-center">
                          <span className="text-2xl sm:text-4xl font-bold text-blue-500">{storeData.brandName.charAt(0)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Banner Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                      <label className="text-sm font-medium text-gray-600">Store Banner</label>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowBannerStatusDialog(true)}
                          className={`relative w-10 h-5 sm:w-12 sm:h-6 rounded-full transition-colors duration-200 ${
                            storeData.isBannerActive ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <div className={`absolute top-0.5 left-0.5 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-white shadow-md transition-transform duration-200 ${
                            storeData.isBannerActive ? 'translate-x-5 sm:translate-x-6' : 'translate-x-0'
                          }`} />
                        </button>
                        <span className={`text-xs sm:text-sm font-medium ${storeData.isBannerActive ? 'text-green-600' : 'text-gray-500'}`}>
                          {storeData.isBannerActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="aspect-[21/9] bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                      {storeData.bannerUrl ? (
                        <img src={storeData.bannerUrl} alt="Store Banner" className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400 text-sm sm:text-base">
                          No banner image
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Social Media */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden lg:sticky lg:top-24">
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Social Media</h2>
                <div className="space-y-3 sm:space-y-4">
                  {Object.entries(socialLinks).map(([platform, url]) => (
                    <div
                      key={platform}
                      className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100 hover:bg-gray-100 transition-colors"
                    >
                      {platform === "facebook" && <Facebook className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />}
                      {platform === "instagram" && <Instagram className="w-5 h-5 sm:w-6 sm:h-6 text-pink-600" />}
                      {platform === "twitter" && <Twitter className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />}
                      <div className="flex-1 min-w-0">
                        <span className="text-gray-800 text-sm font-medium capitalize block">{platform}</span>
                        <span className="text-gray-500 text-xs sm:text-sm truncate block">
                          {url || 'Not connected'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Banner Status Dialog */}
      <ConfirmDialog
        isOpen={showBannerStatusDialog}
        onCancel={() => setShowBannerStatusDialog(false)}
        onConfirm={handleToggleBanner}
        title={`${storeData.isBannerActive ? "Disable" : "Enable"} Store Banner`}
        message={`Are you sure you want to ${
          storeData.isBannerActive ? "disable" : "enable"
        } the store banner?`}
        confirmLabel={storeData.isBannerActive ? "Disable Banner" : "Enable Banner"}
      />
    </div>
  );
};

export default StoreConfig;